import React from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  CasesCredCaseFileUploadWithGenerateVerificationFormButtonContainerCredentialVerificationCaseV1UpdateDocument,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument,
  CredentialVerificationCaseV1UpdateInput,
  FileInfo
} from '../../../../../../generated';
import { GetCredCaseAuthHeader } from './cred-case-get-auth-header';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { FileUploadWithGenerateVerificationFormButton } from './file-upload-with-generate-verification-form-button';

interface FileUploadWithGenerateVerificationFormButtonContainerProps {
  description?: string;
  caseId: string;
  title?: string;
  buttonText?: string;
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  fileName: string;
  onUploadSuccess?: (response: AzureBlobResponse) => Promise<void>; // after upload and fetch preview data success
  isShowFileName?: boolean;
}

export const FileUploadWithGenerateVerificationFormButtonContainer: React.FC<FileUploadWithGenerateVerificationFormButtonContainerProps> = ({
  isPreviewOnly = false,
  ...props
}) => {
  const [getAuthHeaderForPdfFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument
  );

  const [getAuthHeaderForImageFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument
  );

  const [getFilePreviewData] = useLazyQuery(CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document, {
    fetchPolicy: 'network-only'
  });

  const [updatedCredVerificationCaseV1] = useMutation(CasesCredCaseFileUploadWithGenerateVerificationFormButtonContainerCredentialVerificationCaseV1UpdateDocument);

  const handleUpdateAfterUploadSuccess = async (uploadedData: AzureBlobResponse) => {
    const assetPath = 'private';
    const input: CredentialVerificationCaseV1UpdateInput = {
      id: props.caseId,
      assets: {
        [assetPath]: {
          verificationForm: uploadedData.blobUrl,
          verificationFormVersion: uploadedData.versionId
        }
      }
    };
    await updatedCredVerificationCaseV1({
      variables: {
        input
      }
    });
  };

  const handleAuthorizeRequest = async (fileData: File, filename: string): Promise<AuthResult> => {
    return GetCredCaseAuthHeader(props.caseId, fileData, filename, getAuthHeaderForPdfFile, getAuthHeaderForImageFile);
  };

  const handleFetchFilePreviewData = async (filename: string): Promise<FileInfo> => {
    const previewData = await getFilePreviewData({
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
        fileName: filename
      }
    });
    return previewData?.data?.credentialVerificationCaseV1?.fileByFileName as FileInfo;
  };

  return (
    <FileUploadWithGenerateVerificationFormButton
      caseId={props.caseId}
      title={props.title}
      buttonText={props.buttonText}
      description={props.description}
      permittedContentTypes={props.permittedContentTypes}
      permittedExtensions={props.permittedExtensions}
      isPreviewOnly={isPreviewOnly}
      setIsEditing={props.setIsEditing}
      onAuthorizeRequest={(fileData: File) => handleAuthorizeRequest(fileData, props.fileName)}
      fetchFilePreviewData={() => handleFetchFilePreviewData(props.fileName)}
      onUploadSuccess={(response: AzureBlobResponse) => handleUpdateAfterUploadSuccess(response)}
      isShowFileName={props.isShowFileName}
      isUploadingPreviewImage={false}
    />
  );
};
